/**
 * How an admin list of organizations or programs sorts: by name, or by how
 * many coaches a row has. The public count leads because it is the number the
 * row's own public page shows.
 *
 * Aliases: organization sort, cco sort, program sort, ctp sort, admin organizations order, coach count sort
 */

import { z } from "zod";

import {
  nextSortSearch,
  SORT_DIRECTIONS,
  type SortDefaults,
  type SortDirection,
} from "~/lib/shared/sort";

export const COACH_COUNT_SORTS = ["name", "coaches"] as const;
export type CoachCountSort = (typeof COACH_COUNT_SORTS)[number];

// A stale or hand-edited link drops the params it gets wrong instead of
// failing the page: `?sort=nonsense` shows the default name order.
export const coachCountSearchSchema = z.object({
  sort: z.enum(COACH_COUNT_SORTS).optional().catch(undefined),
  dir: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
});

/** Names read best A-Z; the coach count starts with the most coaches. */
export const COACH_COUNT_SORT_DEFAULTS: SortDefaults<CoachCountSort> = {
  sort: "name",
  direction: (sort) => (sort === "name" ? "asc" : "desc"),
};

/** The sort the page shows for its address: what the address says, else the defaults. */
export function resolveCoachCountSort(
  search: z.infer<typeof coachCountSearchSchema>,
): { sort: CoachCountSort; direction: SortDirection } {
  const sort = search.sort ?? COACH_COUNT_SORT_DEFAULTS.sort;
  return {
    sort,
    direction: search.dir ?? COACH_COUNT_SORT_DEFAULTS.direction(sort),
  };
}

/**
 * The address update for a click on `column`'s heading while `current` shows:
 * the other address params stay, and the default view stays out of the address.
 */
export function nextCoachCountSortSearch(
  current: { sort: CoachCountSort; direction: SortDirection },
  column: CoachCountSort,
): <Previous extends object>(
  previous: Previous,
) => Previous & ReturnType<typeof nextSortSearch<CoachCountSort>> {
  return (previous) => ({
    ...previous,
    ...nextSortSearch(current, column, COACH_COUNT_SORT_DEFAULTS),
  });
}

interface CoachCountSortFields {
  name: string;
  claimedCoachCount: number;
  publicCoachCount: number;
}

// One fixed locale so the server render and every browser agree; base
// sensitivity ignores accents and capitals the way the alphabet does.
const nameCollator = new Intl.Collator("en", { sensitivity: "base" });

function compareNames(
  left: CoachCountSortFields,
  right: CoachCountSortFields,
): number {
  return nameCollator.compare(left.name, right.name);
}

function compareCoachCounts(
  left: CoachCountSortFields,
  right: CoachCountSortFields,
): number {
  return (
    left.publicCoachCount - right.publicCoachCount ||
    left.claimedCoachCount - right.claimedCoachCount
  );
}

/**
 * The rows in the requested order, as a new array. Coaches orders by the public
 * count, then the claimed count, and both follow the direction; equal counts
 * fall back to the name, which always reads A-Z.
 */
export function sortByCoachCount<Row extends CoachCountSortFields>(
  rows: readonly Row[],
  sort: CoachCountSort,
  direction: SortDirection,
): Row[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    switch (sort) {
      case "name":
        return sign * compareNames(left, right);
      case "coaches":
        return (
          sign * compareCoachCounts(left, right) || compareNames(left, right)
        );
      default: {
        const exhaustive: never = sort;
        throw new Error(`Unhandled coach count sort: ${String(exhaustive)}`);
      }
    }
  });
}
